import { FormattedMessage, useIntl } from 'react-intl';
import { ActiveMembership, LOADING_TYPES } from '../../../utils/checkoutState';
import Button from '../../atoms/Button';
import FormattedCurrency from '../../atoms/FormattedCurrency';
import SubHeadline from '../../atoms/SubHeadline';
import ToolTip from '../../atoms/ToolTip';

type PointsRedemptionProps = {
  activeMembership: ActiveMembership;
  redeem: (redeemablePointBalance: number) => Promise<void>;
  unredeem: () => Promise<void>;
  isLoading: string;
};

const PointsRedemption = ({ activeMembership, redeem, unredeem, isLoading }: PointsRedemptionProps) => {
  const { totalPointBalance, redeemablePointBalance, redeemedPoints, remainingPointBalance } = activeMembership;
  const { formatMessage } = useIntl();
  const hasRedeemedAlready = !!redeemedPoints;

  const redeemHandler = async () => {
    if (hasRedeemedAlready) {
      await unredeem();
    } else {
      await redeem(redeemablePointBalance);
    }
  };

  const infoText = <FormattedMessage id="payment.loyaltyProgram.redeemablePoints.info" />;
  const noPointsInfo = <FormattedMessage id="payment.loyaltyProgram.redeemablePoints.noPointsInfo" />;
  const redeemedInfoText = (
    <FormattedMessage
      id="payment.loyaltyProgram.redeemablePoints.redeemedInfo"
      values={{
        redeemedPoints,
        redeemablePointBalanceInCurrency: <FormattedCurrency value={activeMembership.redeemedPointsInCurrency!} />
      }}
    />
  );

  const balanceText = (
    <FormattedMessage id="payment.loyaltyProgram.redeemablePoints.pointBalance" values={{ totalPointBalance }} />
  );
  const toolTipText = formatMessage(
    { id: 'payment.loyaltyProgram.redeemablePoints.tooltip' },
    { redeemablePointBalance }
  );
  const remainingBalanceText = (
    <FormattedMessage
      id="payment.loyaltyProgram.redeemablePoints.remainingPointBalance"
      values={{
        remainingPointBalance
      }}
    />
  );

  const redeemButtonText = (
    <FormattedMessage
      id="payment.loyaltyProgram.redeemablePoints.redeemButton"
      values={{
        redeemablePointBalance,
        redeemablePointBalanceInCurrency: (
          <FormattedCurrency value={activeMembership.redeemablePointBalanceInCurrency} />
        )
      }}
    />
  );
  const doNotRedeemButtonText = <FormattedMessage id="payment.loyaltyProgram.redeemablePoints.doNotRedeemButton" />;

  const hasEnoughPoints = !(
    totalPointBalance === 0 ||
    activeMembership.redeemablePointBalance === 0 ||
    activeMembership.redeemablePointBalanceInCurrency === 0 ||
    totalPointBalance < redeemablePointBalance
  );
  const canRedeemWithoutSurplus = totalPointBalance === redeemablePointBalance;
  const showToolTip = hasEnoughPoints && !canRedeemWithoutSurplus && !hasRedeemedAlready;

  const infoMessage = hasRedeemedAlready ? redeemedInfoText : infoText;
  const balanceMessage =
    !hasEnoughPoints ? noPointsInfo
    : hasRedeemedAlready ? remainingBalanceText
    : balanceText;
  const buttonMessage = !hasRedeemedAlready ? redeemButtonText : doNotRedeemButtonText;

  return (
    <>
      <SubHeadline>
        <FormattedMessage id="payment.loyaltyProgram.redeemablePoints.headline" />
      </SubHeadline>
      <p className="loyalty-program__redemption-info">{infoMessage}</p>
      <div className="loyalty-program__redemption-balance-container">
        <p className="loyalty-program__redemption-balance">{balanceMessage}</p>
        {showToolTip && <ToolTip content={toolTipText} dataTestId="loyalty-program-redemption" />}
      </div>
      {hasEnoughPoints && (
        <Button
          className="loyalty-program__redemption-button"
          onClick={redeemHandler}
          isLoading={
            hasRedeemedAlready ? isLoading === LOADING_TYPES.loyaltyUnredeem : isLoading === LOADING_TYPES.loyaltyRedeem
          }
          secondary={hasRedeemedAlready}
          disabled={!!isLoading}
        >
          {buttonMessage}
        </Button>
      )}
    </>
  );
};

export default PointsRedemption;
